import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UNPUBLISH_REASONS } from '@/lib/reason-codes';

interface UnpublishConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the optional coded reason and note (FR-178); both null when none was given. */
  onConfirm: (reason: { reasonCode: string | null; note: string | null }) => void;
  isLoading: boolean;
  locationName: string;
}

export function UnpublishConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
  locationName,
}: UnpublishConfirmationDialogProps): JSX.Element {
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');

  // The dialog stays mounted while closed: clear the reason and note on every close (Cancel,
  // Escape, the overlay, or the parent closing it after an unpublish), so a re-open never
  // shows or re-submits the previous values.
  useEffect(() => {
    if (!open) {
      setReasonCode('');
      setNote('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="unpublish-confirmation-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <DialogTitle>Unpublish Location</DialogTitle>
          </div>
          <DialogDescription>
            Are you sure you want to unpublish &quot;{locationName}&quot;?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
          <p className="text-sm">This will:</p>
          <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
            <li>Remove the location from ScoutAround immediately</li>
            <li>Make it no longer discoverable by users</li>
            <li>Cancel any pending bookings for this location</li>
          </ul>
          <p className="text-sm font-medium">
            You can republish the location at any time after unpublishing.
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="unpublish-reason" className="text-sm font-medium">
            Reason (optional)
          </label>
          <select
            id="unpublish-reason"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            value={reasonCode}
            onChange={(event) => { setReasonCode(event.target.value); }}
            disabled={isLoading}
            data-testid="unpublish-reason-select"
          >
            <option value="">No reason given</option>
            {UNPUBLISH_REASONS.map((reason) => (
              <option key={reason.code} value={reason.code}>
                {reason.label}
              </option>
            ))}
          </select>
          <label htmlFor="unpublish-reason-note" className="block text-sm font-medium">
            Note (optional)
          </label>
          <textarea
            id="unpublish-reason-note"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            maxLength={500}
            rows={2}
            value={note}
            onChange={(event) => { setNote(event.target.value); }}
            disabled={isLoading}
            data-testid="unpublish-reason-note"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              onConfirm({ reasonCode: reasonCode === '' ? null : reasonCode, note: note.trim() === '' ? null : note.trim() });
            }}
            disabled={isLoading}
            data-testid="confirm-unpublish-btn"
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Unpublish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
